import { useEffect, useMemo, useRef, useState } from "react";
import { Send, X, Sparkles, MessageSquare, Maximize2, Minimize2, PhoneCall } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { BRAND } from "@/lib/brand";
import { useI18n } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "luxenova.agent.session";
const HISTORY_KEY = "luxenova.agent.messages";

function loadHistory() {
    try {
        const raw = localStorage.getItem(HISTORY_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (_) {
        return null;
    }
}

function saveHistory(messages) {
    try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-50)));
    } catch (_) {
        /* ignore */
    }
}

const mkMsg = (role, content, extra = {}) => ({
    id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
    role,
    content,
    ...extra,
});

export default function ChatbotWidget() {
    const { t, lang } = useI18n();
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY));
    const [messages, setMessages] = useState(() => loadHistory() || [
        mkMsg("assistant", t("chatbot.welcome")),
    ]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [escalate, setEscalate] = useState(false);
    const bottomRef = useRef(null);

    useEffect(() => {
        // Refresh welcome copy when language changes and no interaction yet
        setMessages((prev) =>
            prev.length === 1 && prev[0].role === "assistant"
                ? [mkMsg("assistant", t("chatbot.welcome"))]
                : prev
        );
    }, [lang, t]);

    useEffect(() => saveHistory(messages), [messages]);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open, busy]);

    const suggestions = useMemo(() => t("chatbot.suggestions") || [], [t]);

    const send = async (e, override) => {
        e?.preventDefault();
        const text = (override ?? input).trim();
        if (!text || busy) return;
        setInput("");
        setMessages((m) => [...m, mkMsg("user", text)]);
        setBusy(true);
        try {
            const res = await fetch(`${API}/agent/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId, message: text, language: lang }),
            });
            if (!res.ok) throw new Error("Concierge unavailable");
            const data = await res.json();
            if (data.session_id && data.session_id !== sessionId) {
                setSessionId(data.session_id);
                localStorage.setItem(SESSION_KEY, data.session_id);
            }
            setMessages((m) => [...m, mkMsg("assistant", data.text || "", {
                cards: data.property_cards || [],
            })]);
            setEscalate(Boolean(data.escalate));
        } catch (_) {
            setMessages((m) => [...m, mkMsg("assistant", t("chatbot.error"))]);
            setEscalate(true);
        } finally {
            setBusy(false);
        }
    };

    const clearConversation = () => {
        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(HISTORY_KEY);
        setSessionId(null);
        setEscalate(false);
        setMessages([mkMsg("assistant", t("chatbot.welcome"))]);
    };

    const panelWidth = expanded ? "sm:max-w-3xl md:max-w-4xl" : "sm:max-w-md";

    return (
        <>
            {!open && (
                <button
                    data-testid="chatbot-fab-trigger"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-6 right-6 z-40 h-14 w-14 md:h-16 md:w-16 rounded-full bg-[#C9A96E] text-[#050D1C] flex items-center justify-center shadow-[0_18px_45px_rgba(0,0,0,0.5)] pulse-gold hover:scale-105 transition-transform"
                    aria-label="Open LuxeNova concierge"
                >
                    <MessageSquare size={22} strokeWidth={1.8} />
                </button>
            )}

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    data-testid="chatbot-drawer-content"
                    side="right"
                    className={`bg-[#050D1C] border-l border-[#C9A96E]/25 text-slate-200 w-full ${panelWidth} p-0 flex flex-col`}
                >
                    <VisuallyHidden>
                        <SheetTitle>LuxeNova AI Concierge</SheetTitle>
                        <SheetDescription>
                            Chat with LuxeNova's AI concierge to find properties, book stays, request maintenance and more.
                        </SheetDescription>
                    </VisuallyHidden>
                    <div className="px-6 py-5 border-b border-[#C9A96E]/15 flex items-center gap-3">
                        <img src={BRAND.logo} alt="LuxeNova" className="h-10 w-10 rounded-full ring-1 ring-[#C9A96E]/50" />
                        <div className="flex-1 min-w-0">
                            <div className="font-serif-display text-white text-lg truncate">{t("chatbot.title")}</div>
                            <div className="text-[10px] tracking-[0.28em] uppercase text-[#C9A96E]">
                                <Sparkles size={10} className="inline mr-1" /> {t("chatbot.status")}
                            </div>
                        </div>
                        <button
                            data-testid="chatbot-expand"
                            onClick={() => setExpanded((s) => !s)}
                            className="text-slate-400 hover:text-white p-1 hidden sm:inline-flex"
                            aria-label="Toggle expand"
                        >
                            {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                        </button>
                        <button data-testid="chatbot-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white p-1" aria-label="Close">
                            <X size={18} />
                        </button>
                    </div>

                    <div data-testid="chatbot-messages" className="flex-1 overflow-y-auto px-5 py-6 space-y-4">
                        {messages.map((m, i) => (
                            <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[92%] ${m.role === "user" ? "" : "w-full"}`}>
                                    <div
                                        className={`chat-bubble px-4 py-3 rounded-[10px] text-sm leading-relaxed whitespace-pre-wrap ${
                                            m.role === "user"
                                                ? "bg-[#C9A96E] text-[#050D1C] inline-block"
                                                : "bg-[#0A1A38] border border-[#C9A96E]/15 text-slate-200"
                                        }`}
                                    >
                                        {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                                    </div>
                                    {m.cards && m.cards.length > 0 && (
                                        <div className={`mt-3 grid gap-3 ${expanded ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
                                            {m.cards.map((p) => (
                                                <PropertyCard key={p.id || p.slug} p={p} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {busy && (
                            <div className="flex justify-start" data-testid="chatbot-typing">
                                <div className="bg-[#0A1A38] border border-[#C9A96E]/15 px-4 py-3 rounded-[10px] inline-flex items-center gap-1.5">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#C9A96E] animate-[bounce_1.2s_ease-in-out_infinite]" />
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#C9A96E] animate-[bounce_1.2s_ease-in-out_infinite] [animation-delay:120ms]" />
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#C9A96E] animate-[bounce_1.2s_ease-in-out_infinite] [animation-delay:240ms]" />
                                </div>
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>

                    {escalate && (
                        <div className="px-5 pb-3">
                            <a
                                data-testid="chatbot-callback-cta"
                                href={`https://wa.me/${BRAND.whatsappRaw}?text=${encodeURIComponent(
                                    lang === "pl" ? "Dzień dobry LuxeNova — poproszę o kontakt telefoniczny." : "Hello LuxeNova — I'd like to request a callback."
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 w-full py-3 border border-[#C9A96E] text-[#C9A96E] hover:bg-[#C9A96E] hover:text-[#050D1C] transition-colors text-[11px] tracking-[0.28em] uppercase"
                            >
                                <PhoneCall size={13} /> {lang === "pl" ? "Poproś o oddzwonienie" : "Request a Callback"}
                            </a>
                        </div>
                    )}

                    {messages.length <= 1 && (
                        <div className="px-5 pb-3 flex flex-wrap gap-2">
                            {suggestions.map((s, i) => (
                                <button
                                    key={s}
                                    data-testid={`chatbot-suggestion-${i}`}
                                    onClick={(e) => send(e, s)}
                                    className="text-xs px-3 py-1.5 border border-[#C9A96E]/30 text-[#C9A96E] hover:bg-[#C9A96E]/10 transition"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    )}

                    <form onSubmit={send} className="px-5 py-4 border-t border-[#C9A96E]/15 flex items-center gap-3">
                        <input
                            data-testid="chatbot-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={t("chatbot.placeholder")}
                            className="flex-1 bg-[#0A1A38] border border-[#C9A96E]/25 rounded-sm px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#C9A96E]/70"
                            disabled={busy}
                        />
                        <button
                            data-testid="chatbot-send"
                            type="submit"
                            disabled={busy || !input.trim()}
                            className="h-11 w-11 flex items-center justify-center bg-[#C9A96E] text-[#050D1C] rounded-sm hover:bg-[#B8925A] disabled:opacity-50 transition"
                        >
                            <Send size={16} />
                        </button>
                    </form>
                    <div className="px-5 pb-4 flex items-center justify-between text-[10px] tracking-[0.28em] uppercase text-slate-500">
                        <button data-testid="chatbot-clear" onClick={clearConversation} className="hover:text-[#C9A96E]">
                            {lang === "pl" ? "Wyczyść rozmowę" : "Clear chat"}
                        </button>
                        <span>LuxeNova · AI Concierge</span>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}

function PropertyCard({ p }) {
    const price = p.price_eur ? `€${Number(p.price_eur).toLocaleString()}` : null;
    const suffix = p.listing_type === "rent" ? "/ mo" : "";
    return (
        <a
            href={`#contact`}
            data-testid={`chatbot-property-${p.id || p.slug}`}
            className="block glass-card overflow-hidden hover:border-[#C9A96E]/60 transition-colors"
        >
            {p.images?.[0] && (
                <div className="h-32 overflow-hidden">
                    <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
            )}
            <div className="p-4">
                <div className="flex items-center gap-2 text-[9px] tracking-[0.24em] uppercase text-[#C9A96E]">
                    <span>{p.property_type}</span>
                    <span className="text-slate-500">·</span>
                    <span className="text-slate-500">{p.listing_type}</span>
                </div>
                <div className="mt-1 font-serif-display text-white text-base leading-tight">{p.title}</div>
                <div className="mt-1 text-xs text-slate-400">{p.city}{p.bedrooms ? ` · ${p.bedrooms} BR` : ""}{p.area_sqm ? ` · ${p.area_sqm} m²` : ""}</div>
                {price && (
                    <div className="mt-2 text-[#C9A96E] text-sm">
                        {price} <span className="text-slate-500 text-xs">{suffix}</span>
                    </div>
                )}
            </div>
        </a>
    );
}
